import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { TaskTable } from "@/components/TaskTable";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

/**
 * Table / grid view — spreadsheet-style task list with show/hide/reorder
 * columns, multi-column sort, combined filters, per-project saved views
 * (localStorage), and CSV export. Sits beside the Board and List leaf routes
 * under the shared project shell (`route.tsx` renders the <Outlet />).
 */
function TableView() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();

  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const fieldsQ = useQuery({
    queryKey: queryKeys.customFields(projectId),
    queryFn: () => api.listCustomFields(projectId),
  });
  const cfValuesQ = useQuery({
    queryKey: queryKeys.customFieldValues(projectId),
    queryFn: () => api.customFieldValues(projectId),
  });

  const users = useMemo(() => {
    const map = new Map<string, { name: string; image: string | null }>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, {
        name: m.name ?? m.email ?? m.id.slice(0, 8),
        image: m.image ?? null,
      });
    }
    return map;
  }, [teamQ.data]);

  return (
    <div className="proj-shell-content">
      <div className="board-bar" style={{ marginBottom: 12 }}>
        <SegmentedToggle
          options={[
            { value: "board", label: "Board", icon: "board" },
            { value: "list", label: "List", icon: "clip" },
            { value: "table", label: "Table", icon: "grid" },
            { value: "calendar", label: "Calendar", icon: "cal" },
            { value: "timeline", label: "Timeline", icon: "arrow" },
          ]}
          value="table"
          onChange={(v) =>
            navigate({
              to:
                v === "list"
                  ? "/projects/$projectId/list"
                  : v === "calendar"
                    ? "/projects/$projectId/calendar"
                    : v === "timeline"
                      ? "/projects/$projectId/timeline"
                      : v === "board"
                        ? "/projects/$projectId"
                        : "/projects/$projectId/table",
              params: { projectId },
            })
          }
        />
      </div>

      {tasksQ.isLoading ? (
        <Skeleton count={8} height={40} />
      ) : tasksQ.isError ? (
        <EmptyState
          icon="close"
          title="Couldn't load this project."
          caption="Refresh the page or return to the project list."
        />
      ) : (tasksQ.data?.tasks ?? []).length === 0 ? (
        <EmptyState
          icon="check"
          title="No tasks yet."
          caption="Add a task from the Board view to get started."
        />
      ) : (
        <TaskTable
          projectId={projectId}
          tasks={tasksQ.data?.tasks ?? []}
          users={users}
          fields={fieldsQ.data?.fields ?? []}
          values={cfValuesQ.data?.values ?? []}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/table")({
  component: TableView,
});
